import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { contactAPI } from '../../services/api';
import {
  Clock, Zap, Shield, Mail, Phone, MapPin, ArrowRight, CheckCircle, Play,
  Package, FileText, Send, MessageCircle, ArrowDown, Download,
  ClipboardCheck, Factory, BookOpen,
} from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { usePublicContent, mergeHero, mergeProducts } from '../../hooks/usePublicContent';
import './KinetiCore.css';

const MeccaFlex = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'en' ? 'en' : 'it';
  const { dbContent } = usePublicContent(lang);
  const hero = mergeHero(t('hero', { returnObjects: true }), dbContent);
  const products = mergeProducts(t('products', { returnObjects: true }), dbContent);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [countersVisible, setCountersVisible] = useState(false);
  const [counts, setCounts] = useState({ hours: 0 });
  const [activeIndustry, setActiveIndustry] = useState(0);
  const statsRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', company: '', urgency: 'urgent', message: '',
    language: i18n.language === 'en' ? 'en' : 'it'
  });
  const [formStatus, setFormStatus] = useState({ loading: false, error: '', success: false });
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setFormData(prev => ({ ...prev, language: lng === 'en' ? 'en' : 'it' }));
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      if (statsRef.current) {
        const rect = statsRef.current.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.8 && !countersVisible) setCountersVisible(true);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [countersVisible]);

  useEffect(() => { if (countersVisible) setCounts({ hours: 5 }); }, [countersVisible]);

  const scrollToSection = (sectionId) => {
    const el = document.getElementById(sectionId);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  const serviceLines = t('servicesLines.lines', { returnObjects: true }) || [];
  const ratePackages = t('rateCard.packages', { returnObjects: true }) || [];
  const timeBased = t('rateCard.timeBased', { returnObjects: true }) || [];
  const howSteps = [
    { num: '01', Icon: ClipboardCheck, timeKey: 'step1.time', titleKey: 'step1.title', descKey: 'step1.desc' },
    { num: '02', Icon: FileText, timeKey: 'step2.time', titleKey: 'step2.title', descKey: 'step2.desc' },
    { num: '03', Icon: Shield, timeKey: 'step3.time', titleKey: 'step3.title', descKey: 'step3.desc' },
    { num: '04', Icon: Factory, timeKey: 'step4.time', titleKey: 'step4.title', descKey: 'step4.desc' },
    { num: '05', Icon: CheckCircle, timeKey: 'step5.time', titleKey: 'step5.title', descKey: 'step5.desc' },
  ];

  return (
    <div className="landing-container">
      <Header isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} scrollY={scrollY}
        scrollToSection={scrollToSection} currentLanguage={i18n.language} changeLanguage={changeLanguage} />

      <section id="hero" className="hero">
        <div className="hero-bg"><div className="hero-gradient"></div></div>
        <div className="hero-layout">
          <div className="hero-content">
            <h1 className="hero-title">
              <span className="title-top">{hero.title1}</span>
              <span className="title-main">{hero.title2}</span>
            </h1>
            <p className="hero-subtitle">{hero.subtitle}<strong> {hero.subtitleBold}</strong></p>
            <div className="hero-ctas">
              <button className="cta-primary" onClick={() => scrollToSection('contact')}>
                <span className="btn-shimmer" aria-hidden="true" />
                <span>{hero.ctaPrimary}</span>
                <ArrowRight size={15} className="btn-arrow" />
              </button>
              <button className="cta-ghost" onClick={() => scrollToSection('come-funziona')}>
                <span className="cta-ghost-icon"><Play size={12} /></span>
                <span>{hero.ctaSecondary}</span>
              </button>
            </div>
            <div className="hero-trust">
              <div className="trust-chip"><ClipboardCheck size={14} className="chip-icon" /><div className="trust-chip-text"><strong>{hero.chip1val}</strong><span>{hero.trust1}</span></div></div>
              <div className="trust-chip"><Package size={14} className="chip-icon" /><div className="trust-chip-text"><strong>{hero.chip2val}</strong><span>{hero.trust2}</span></div></div>
              <div className="trust-chip"><FileText size={14} className="chip-icon" /><div className="trust-chip-text"><strong>{hero.chip3val}</strong><span>{hero.trust3}</span></div></div>
            </div>
          </div>
          <div className="hero-visual">
            <div className="hero-visual-card">
              <div className="hero-visual-header">
                <div className="hv-dot red"></div><div className="hv-dot yellow"></div><div className="hv-dot green"></div>
                <span className="hv-title">qualification_dossier.md</span>
              </div>
              <div className="hero-visual-body malta-dossier">
                <ul className="dossier-list">
                  <li><CheckCircle size={14} /> Material justification</li>
                  <li><CheckCircle size={14} /> Process specification</li>
                  <li><CheckCircle size={14} /> Acceptance criteria</li>
                  <li><CheckCircle size={14} /> Inspection protocol</li>
                  <li><CheckCircle size={14} /> Traceability & limitations</li>
                </ul>
                <div className="hv-stats">
                  <div className="hv-stat"><Shield size={14} /><span>Release gated</span></div>
                  <div className="hv-stat hv-stat-green"><CheckCircle size={14} /><span>Partner audited</span></div>
                  <div className="hv-stat"><FileText size={14} /><span>Net 30</span></div>
                </div>
                <div className="hv-status"><div className="hv-pulse"></div><span>Engineering before production</span></div>
              </div>
            </div>
          </div>
        </div>
        <div className="hero-scroll-hint" onClick={() => scrollToSection('problema')}>
          <span>{hero.scrollHint || t('hero.scrollHint')}</span>
          <ArrowDown size={20} className="bounce" />
        </div>
      </section>

      <section className="stats-section" ref={statsRef}>
        <div className="stats-inner">
          <span className="stat-chip"><strong>{counts.hours}-gate</strong> {t('stats.responseTime')}</span>
          <span className="stat-sep">·</span>
          <span className="stat-chip"><strong>1</strong> {t('stats.satisfaction')}</span>
          <span className="stat-sep">·</span>
          <span className="stat-chip"><strong>Partner</strong> {t('stats.deliveryDays')}</span>
          <span className="stat-sep">·</span>
          <span className="stat-chip"><strong>v3</strong> {t('stats.hiddenFees')}</span>
        </div>
      </section>

      <section id="problema" className="why-section">
        <div className="section-inner">
          <div className="why-header">
            <span className="section-label green center">{t('why.label')}</span>
            <h2 className="why-headline">{t('why.headline')}</h2>
            <p className="why-lead">{t('why.lead')}</p>
          </div>
          <div className="why-comparison">
            <div className="comparison-col comparison-before">
              <div className="comparison-col-header"><span className="comparison-col-tag tag-before">{t('why.before.label')}</span></div>
              {[1,2,3,4].map(i => (
                <div key={i} className="comparison-row row-before"><span className="row-icon-before">✗</span><span>{t(`why.before.item${i}`)}</span></div>
              ))}
            </div>
            <div className="comparison-divider"><div className="divider-line" /><div className="divider-badge"><Zap size={13} /></div><div className="divider-line" /></div>
            <div className="comparison-col comparison-after">
              <div className="comparison-col-header"><span className="comparison-col-tag tag-after">MeccaFlex</span></div>
              {[1,2,3,4].map(i => (
                <div key={i} className="comparison-row row-after"><CheckCircle size={14} className="row-icon-after" /><span>{t(`why.after.item${i}`)}</span></div>
              ))}
            </div>
          </div>
          <div className="why-pills">
            <div className="why-pill"><ClipboardCheck size={17} className="pill-icon" /><strong>Screen</strong><span>{t('why.g1')}</span></div>
            <div className="why-pill-sep" />
            <div className="why-pill"><Package size={17} className="pill-icon" /><strong>1 {t('why.g2unit')}</strong><span>{t('why.g2')}</span></div>
            <div className="why-pill-sep" />
            <div className="why-pill"><MessageCircle size={17} className="pill-icon" /><strong>Free</strong><span>{t('why.g3')}</span></div>
          </div>
        </div>
      </section>

      <section id="servizi" className="services-lines-section">
        <div className="section-inner">
          <span className="section-label center green">{t('servicesLines.label')}</span>
          <h2 className="section-title center">{t('servicesLines.title')}</h2>
          <p className="section-subtitle">{t('servicesLines.subtitle')}</p>
          <div className="services-lines-grid">
            {Array.isArray(serviceLines) && serviceLines.map((line, i) => (
              <div key={i} className="service-line-card">
                <span className="service-line-num">0{i + 1}</span>
                <h3>{line.title}</h3>
                <p>{line.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="come-funziona" className="how-section">
        <div className="section-inner">
          <span className="section-label center">{t('howItWorks.label')}</span>
          <h2 className="section-title center">{t('howItWorks.title')}</h2>
          <p className="section-subtitle">{t('howItWorks.subtitle')}</p>
          <div className="how-steps how-steps-five how-steps-wrap">
            {howSteps.map(({ num, Icon, timeKey, titleKey, descKey }, i) => (
              <div key={num} className="how-step-wrap">
                <div className="how-step-card">
                  <div className="how-step-top">
                    <span className="how-step-index">{num}</span>
                    <div className="how-step-time-badge"><Clock size={11} />{t(`howItWorks.${timeKey}`)}</div>
                  </div>
                  <div className="how-step-icon"><Icon size={22} /></div>
                  <h3>{t(`howItWorks.${titleKey}`)}</h3>
                  <p>{t(`howItWorks.${descKey}`)}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="how-guarantee"><Zap size={15} /><span>{t('howItWorks.guarantee')}</span></div>
        </div>
      </section>

      <section id="rate-card" className="rate-card-section">
        <div className="section-inner">
          <span className="section-label center green">{t('rateCard.label')}</span>
          <h2 className="section-title center">{t('rateCard.title')}</h2>
          <p className="section-subtitle">{t('rateCard.subtitle')}</p>
          <div className="rate-wedge">
            <BookOpen size={20} />
            <div><strong>{t('rateCard.wedgeTitle')}</strong><p>{t('rateCard.wedgeBody')}</p></div>
          </div>
          <h3 className="rate-packages-title">{t('rateCard.packagesTitle')}</h3>
          <div className="rate-packages-grid">
            {Array.isArray(ratePackages) && ratePackages.map((pkg, i) => (
              <div key={i} className="rate-package-card">
                <div className="rate-package-head">
                  <h4>{pkg.name}</h4>
                  <span className="rate-fee rate-fee--subtle">{t('rateCard.onProposal', 'On proposal')}</span>
                </div>
                <p className="rate-blurb">{pkg.blurb}</p>
                <span className="rate-timeline"><Clock size={12} /> {pkg.timeline}</span>
              </div>
            ))}
          </div>
          <div className="rate-time-table">
            {Array.isArray(timeBased) && timeBased.map((row, i) => (
              <div key={i} className="rate-time-row"><span>{row.basis}</span><strong className="rate-fee--subtle">{t('rateCard.onProposal', 'On proposal')}</strong><span className="rate-time-notes">{row.notes}</span></div>
            ))}
          </div>
          <p className="rate-terms-note">{t('rateCard.termsNote')}</p>
          <a className="rate-download-btn" href={t('rateCard.ctaHref')}>
            <Download size={16} />{t('rateCard.cta')}
          </a>
        </div>
      </section>

      <section id="industrie" className="industries-section">
        <div className="section-inner">
          <span className="section-label center green">{t('industries.label')}</span>
          <h2 className="section-title center">{t('industries.title')}</h2>
          {(() => {
            const industries = t('industries.cases', { returnObjects: true });
            if (!Array.isArray(industries) || !industries.length) return null;
            const active = industries[activeIndustry] || industries[0];
            return (
              <>
                <div className="industry-tabs">
                  {industries.map((ind, i) => (
                    <button key={i} className={`industry-tab ${i === activeIndustry ? 'active' : ''}`} onClick={() => setActiveIndustry(i)}>
                      <span>{ind.icon}</span><span>{ind.title}</span>
                    </button>
                  ))}
                </div>
                <div className="industry-panel" key={activeIndustry}>
                  <div className="industry-left">
                    <span className="industry-emoji">{active.icon}</span>
                    <h3 className="industry-title">{active.title}</h3>
                    <div className="industry-pain-badge"><span>✗</span> {active.pain}</div>
                    <p className="industry-pain-detail">{active.painDetail}</p>
                    <span className="industry-urgency-tag"><Zap size={11} /> {active.tag}</span>
                  </div>
                  <div className="industry-right">
                    <p className="industry-examples-label">{t('industries.examplesLabel')}</p>
                    <ul className="industry-examples">
                      {active.examples.map((ex, i) => (
                        <li key={i} className="industry-example"><CheckCircle size={14} className="example-check" /><span>{ex}</span></li>
                      ))}
                    </ul>
                    <button className="industry-cta" onClick={() => scrollToSection('contact')}>{t('industries.cta')} <ArrowRight size={14} /></button>
                  </div>
                </div>
              </>
            );
          })()}
        </div>
      </section>

      <section className="products-section">
        <div className="section-inner">
          <span className="section-label center">{products.label || t('products.label')}</span>
          <h2 className="section-title center">{products.title || t('products.title')}</h2>
          <p className="section-subtitle center">{products.subtitle || t('products.subtitle')}</p>
          <div className="products-grid">
            {(products.categories || t('products.categories', { returnObjects: true }) || []).map((cat, i) => (
              <div key={i} className="product-card">
                <span className="product-card-icon" aria-hidden="true">{['📋','📁','🤝','💾','🎓','⏱'][i] || '•'}</span>
                <h3>{cat.name}</h3><p>{cat.desc}</p>
              </div>
            ))}
          </div>
          <div className="materials-strip">
            <span className="materials-label">{t('products.materials')}</span>
            <div className="materials-chips">
              {(products.materialsList || t('products.materialsList', { returnObjects: true }) || []).map((m, i) => (
                <span key={i} className="material-chip">{m}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="credibility-section">
        <div className="section-inner credibility-inner">
          <span className="section-label green">{t('credibility.label')}</span>
          <h2 className="section-title">{t('credibility.title')}</h2>
          <p className="credibility-body">{t('credibility.body')}</p>
          <div className="credibility-stat"><strong>{t('credibility.stat')}</strong><span>{t('credibility.statNote')}</span></div>
        </div>
      </section>

      <section className="urgency-section">
        <div className="urgency-inner">
          <div className="urgency-bg-text" aria-hidden="true">SCREEN</div>
          <span className="urgency-eyebrow">{t('urgency.eyebrow')}</span>
          <h2 className="urgency-headline">{t('urgency.title')}</h2>
          <p className="urgency-sub">{t('urgency.subtitle')}</p>
          <div className="urgency-chips">
            <div className="urgency-chip"><Clock size={13} /><span>{t('urgency.chip1')}</span></div>
            <div className="urgency-chip"><Shield size={13} /><span>{t('urgency.chip2')}</span></div>
            <div className="urgency-chip"><ArrowRight size={13} /><span>{t('urgency.chip3')}</span></div>
          </div>
          <button className="cta-urgent" onClick={() => scrollToSection('contact')}>
            <span className="btn-shimmer" aria-hidden="true" />
            <ClipboardCheck size={16} />{t('urgency.cta')}
          </button>
          <p className="urgency-note"><Clock size={13} /><span>{t('urgency.note')}</span></p>
        </div>
      </section>

      <section id="contact" className="contact-section">
        <div className="section-inner">
          <div className="contact-grid">
            <div className="contact-info">
              <span className="section-label">{t('contact.label')}</span>
              <h2 className="section-title">{t('contact.title')}</h2>
              <p className="contact-desc">{t('contact.subtitle')}</p>
              <div className="contact-methods">
                <a href="mailto:info@meccaflex.com" className="contact-method"><Mail size={28} /><div><strong>{t('contact.email')}</strong><span>info@meccaflex.com</span></div></a>
                <a href="https://wa.me/393401234567" className="contact-method whatsapp"><MessageCircle size={28} /><div><strong>{t('contact.whatsapp')}</strong><span>{t('contact.whatsappSub')}</span></div></a>
                <a href="tel:+393401234567" className="contact-method"><Phone size={28} /><div><strong>{t('contact.phone')}</strong><span>+39 340 123 4567</span></div></a>
              </div>
              <div className="contact-location"><MapPin size={20} /><span>{t('contact.location')}</span></div>
            </div>
            <div className="contact-form-container">
              <form className="contact-form" onSubmit={async (e) => {
                e.preventDefault();
                setFormStatus({ loading: true, error: '', success: false });
                try {
                  const sectorLabel = formData.urgency === 'urgent' ? 'Maritime / superyacht'
                    : formData.urgency === 'normal' ? 'Electronics manufacturing'
                    : 'Pharma / medical / aviation / other';
                  await contactAPI.submit({
                    ...formData,
                    message: `[Obsolescence screen request · Sector: ${sectorLabel}]\n\n${formData.message}`,
                    company: formData.company || 'Not provided',
                  });
                  setFormStatus({ loading: false, error: '', success: true });
                  setFormData({ name: '', email: '', phone: '', company: '', urgency: 'urgent', message: '', language: i18n.language === 'en' ? 'en' : 'it' });
                  setShowSuccessModal(true);
                } catch (error) {
                  setFormStatus({ loading: false, error: error.response?.data?.message || 'Something went wrong. Please try again or email info@meccaflex.com.', success: false });
                }
              }}>
                <h3>{t('contact.formTitle')}</h3>
                {formStatus.error && <div style={{ padding: '12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', color: '#fca5a5', marginBottom: '16px' }}>{formStatus.error}</div>}
                {formStatus.success && <div style={{ padding: '12px', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: '8px', color: '#6ee7b7', marginBottom: '16px' }}>{t('contact.successMsg')}</div>}
                <div className="form-group"><input type="text" placeholder={t('contact.formName')} value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required /></div>
                <div className="form-group"><input type="text" placeholder={t('contact.formCompany')} value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} required /></div>
                <div className="form-row">
                  <div className="form-group"><input type="email" placeholder={t('contact.formEmail')} value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required /></div>
                  <div className="form-group"><input type="tel" placeholder={t('contact.formPhone')} value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} /></div>
                </div>
                <div className="form-group">
                  <select value={formData.urgency} onChange={(e) => setFormData({ ...formData, urgency: e.target.value })} aria-label={t('contact.formUrgency')}>
                    <option value="urgent">{t('contact.urgentOption')}</option>
                    <option value="normal">{t('contact.normalOption')}</option>
                    <option value="planning">{t('contact.planningOption')}</option>
                  </select>
                </div>
                <div className="form-group"><textarea placeholder={t('contact.formMessage')} rows={4} value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} required /></div>
                <button type="submit" className="submit-btn" disabled={formStatus.loading}><Send size={20} />{formStatus.loading ? 'Sending…' : t('contact.formSubmit')}</button>
                <p className="form-note">{t('contact.formNote')}</p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {showSuccessModal && (
        <div className="success-modal-overlay" onClick={() => setShowSuccessModal(false)}>
          <div className="success-modal" onClick={(e) => e.stopPropagation()}>
            <CheckCircle size={48} className="success-icon" />
            <h3>{t('contact.successTitle')}</h3>
            <p>{t('contact.successMsg')}</p>
            <button onClick={() => setShowSuccessModal(false)}>{t('contact.closeButton')}</button>
          </div>
        </div>
      )}

      <Footer scrollToSection={scrollToSection} />
    </div>
  );
};

export default MeccaFlex;
